import React, { forwardRef, useCallback, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    LinearProgress,
    Link,
    TextField,
    TextFieldProps,
    Tooltip,
    Typography,
} from "@mui/material";
import { Close, FactCheckOutlined } from "@mui/icons-material";
import { DeliveryRecord, courierService } from "../../services/courierService";
import { tryNormalizeBdPhone } from "../../utils/phone";

/*
 * On-demand Steadfast delivery record for a phone number, used in the order
 * form. Nothing is looked up until staff press Check: each lookup spends a
 * small allowance on Steadfast's side. Results are kept while the form is
 * open, so pressing Check again on the same number doesn't spend another.
 */

/** Delivered share (of finished parcels) at or above which a record reads as good / mixed. */
const GOOD_RECORD_FROM = 80;
const MIXED_RECORD_FROM = 50;

type CheckState =
    | { status: "idle" }
    | { status: "loading"; phone: string }
    | { status: "done"; phone: string; record: DeliveryRecord; checkedAt: number }
    | { status: "error"; phone: string; message: string };

function useDeliveryRecordCheck() {
    const [state, setState] = useState<CheckState>({ status: "idle" });
    // Results by number, kept while the form is open.
    const results = useRef(new Map<string, { record: DeliveryRecord; checkedAt: number }>());
    const latest = useRef(0);

    const check = useCallback(async (phone: string, force = false) => {
        const saved = results.current.get(phone);
        if (saved && !force) {
            setState({ status: "done", phone, ...saved });
            return;
        }
        const request = ++latest.current;
        setState({ status: "loading", phone });
        try {
            const record = await courierService.checkDeliveryRecord(phone);
            const entry = { record, checkedAt: Date.now() };
            results.current.set(phone, entry);
            if (request === latest.current) setState({ status: "done", phone, ...entry });
        } catch (err: any) {
            if (request !== latest.current) return;
            const detail = err?.response?.data?.detail;
            setState({
                status: "error",
                phone,
                message:
                    typeof detail === "string"
                        ? detail
                        : err?.code === "ECONNABORTED"
                          ? "Steadfast didn't answer in time. Try again."
                          : "Couldn't check this number. Try again.",
            });
        }
    }, []);

    const dismiss = useCallback(() => setState({ status: "idle" }), []);

    return { state, check, dismiss };
}

type DeliveryRecordCheckHandle = ReturnType<typeof useDeliveryRecordCheck>;

// ── Wording ─────────────────────────────────────────────────────────────

function recordLabel(record: DeliveryRecord): {
    label: string;
    color: "success" | "warning" | "error" | "default";
} {
    const delivered = record.delivery_ratio;
    if (delivered === null || delivered === undefined) return { label: "No record yet", color: "default" };
    if (delivered >= GOOD_RECORD_FROM) return { label: "Good record", color: "success" };
    if (delivered >= MIXED_RECORD_FROM) return { label: "Mixed record", color: "warning" };
    return { label: "Poor record", color: "error" };
}

function parcelsText(range?: string | null): string | null {
    if (!range) return null;
    return range === "1" ? "1 parcel" : `${range} parcels`;
}

function figuresText(record: DeliveryRecord): string {
    const parts: string[] = [];
    if (record.delivery_ratio !== null && record.delivery_ratio !== undefined) {
        parts.push(`${record.delivery_ratio}% delivered`);
    }
    if (record.cancellation_ratio !== null && record.cancellation_ratio !== undefined) {
        parts.push(`${record.cancellation_ratio}% cancelled`);
    }
    const parcels = parcelsText(record.volume_range);
    if (parcels) parts.push(parcels);
    return parts.length ? parts.join(" · ") : "No finished parcels for this number yet.";
}

function countsText(record: DeliveryRecord): string | null {
    const parts: string[] = [];
    if (record.delivered_count !== null && record.delivered_count !== undefined) {
        parts.push(`${record.delivered_count} delivered`);
    }
    if (record.cancelled_count !== null && record.cancelled_count !== undefined) {
        parts.push(`${record.cancelled_count} cancelled`);
    }
    return parts.length ? parts.join(", ") : null;
}

function reportsText(record: DeliveryRecord): string | null {
    const total = record.total_reports ?? 0;
    if (total <= 0) return null;
    const categories = Object.entries(record.fraud_categories || {});
    const named = categories.reduce((sum, [, times]) => sum + times, 0);
    const parts = categories.map(([code, times]) => `${code.replace(/_/g, " ")} ×${times}`);
    if (total > named) parts.push(`${total - named} without a reason`);
    return `${total} ${total === 1 ? "report" : "reports"} from other merchants: ${parts.join(", ")}`;
}

function checkedAgo(checkedAt: number): string {
    const seconds = Math.round((Date.now() - checkedAt) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    return new Date(checkedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ── Pieces ──────────────────────────────────────────────────────────────

function CheckButton({
    phone,
    handle,
    invalidReason,
}: {
    /** The normalized number, or null when the field isn't a valid BD mobile. */
    phone: string | null;
    handle: DeliveryRecordCheckHandle;
    invalidReason: string;
}) {
    const loading = handle.state.status === "loading" && handle.state.phone === phone;
    return (
        <Tooltip title={phone ? "Check this number's delivery record with Steadfast" : invalidReason}>
            <span>
                <Button
                    size="small"
                    startIcon={<FactCheckOutlined fontSize="small" />}
                    disabled={!phone || loading}
                    onClick={() => phone && void handle.check(phone)}
                    sx={{ whiteSpace: "nowrap", minWidth: 0 }}
                >
                    {loading ? "Checking…" : "Check"}
                </Button>
            </span>
        </Tooltip>
    );
}

function DeliveryRecordPanel({ phone, handle }: { phone: string | null; handle: DeliveryRecordCheckHandle }) {
    const { state } = handle;
    if (!phone || state.status === "idle" || state.phone !== phone) return null;

    if (state.status === "loading") {
        return (
            <Box sx={{ mt: 1 }}>
                <LinearProgress sx={{ borderRadius: 1 }} />
            </Box>
        );
    }

    if (state.status === "error") {
        return (
            <Alert severity="warning" onClose={handle.dismiss} sx={{ mt: 1, py: 0 }}>
                {state.message}
            </Alert>
        );
    }

    const { record, checkedAt } = state;
    const look = recordLabel(record);
    const counts = countsText(record);
    const reports = reportsText(record);

    return (
        <Box
            sx={{
                mt: 1,
                p: 1.5,
                border: 1,
                borderColor: look.color === "error" ? "error.light" : "divider",
                borderRadius: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" component="span">
                    Delivery record
                </Typography>
                <Chip
                    size="small"
                    label={look.label}
                    color={look.color}
                    variant={look.color === "default" ? "outlined" : "filled"}
                />
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" aria-label="Hide delivery record" onClick={handle.dismiss}>
                    <Close fontSize="small" />
                </IconButton>
            </Box>
            <Typography variant="body2">{figuresText(record)}</Typography>
            {counts && (
                <Typography variant="caption" color="text.secondary">
                    {counts}
                </Typography>
            )}
            {reports ? (
                <Typography variant="body2" color="error.main">
                    {reports}
                    {record.doubtful_reports ? ". Steadfast marks some of these reports as doubtful." : ""}
                </Typography>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    No reports from other merchants
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
                Checked {phone} with Steadfast · {checkedAgo(checkedAt)} ·{" "}
                <Link component="button" type="button" variant="caption" onClick={() => void handle.check(phone, true)}>
                    Check again
                </Link>
            </Typography>
        </Box>
    );
}

// ── Exported ────────────────────────────────────────────────────────────

/**
 * Drop-in for the order form's phone TextField: same props (spread a
 * react-hook-form `field` into it as before), plus a Check button at its
 * right edge and the delivery record underneath.
 */
export const PhoneFieldWithDeliveryCheck = forwardRef<HTMLDivElement, TextFieldProps>(
    function PhoneFieldWithDeliveryCheck({ InputProps, ...props }, ref) {
        const handle = useDeliveryRecordCheck();
        const phone = tryNormalizeBdPhone(typeof props.value === "string" ? props.value : "");
        return (
            <Box sx={{ width: "100%", minWidth: 0 }}>
                <TextField
                    {...props}
                    ref={ref}
                    InputProps={{
                        ...InputProps,
                        endAdornment: (
                            <InputAdornment position="end">
                                <CheckButton
                                    phone={phone}
                                    handle={handle}
                                    invalidReason="Enter a valid phone number to check it"
                                />
                            </InputAdornment>
                        ),
                    }}
                />
                <DeliveryRecordPanel phone={phone} handle={handle} />
            </Box>
        );
    },
);

function PhoneCheckLine({ label, raw }: { label: string; raw: string }) {
    const handle = useDeliveryRecordCheck();
    const phone = tryNormalizeBdPhone(raw);
    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 84 }}>
                    {label}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {raw}
                </Typography>
                <CheckButton
                    phone={phone}
                    handle={handle}
                    invalidReason="This isn't a valid BD mobile number, so it can't be checked"
                />
            </Box>
            <DeliveryRecordPanel phone={phone} handle={handle} />
        </Box>
    );
}

/**
 * An existing customer's phone and alternative phone, read-only, each with
 * a Check button. Renders nothing when the customer has no phone at all.
 */
export function CustomerPhoneChecks({
    customer,
    title,
}: {
    customer: { phone?: string | null; alternative_phone?: string | null } | null | undefined;
    /** Optional heading line, e.g. the customer's name where the form doesn't show it. */
    title?: React.ReactNode;
}) {
    const lines = [
        { label: "Phone", raw: customer?.phone?.trim() },
        { label: "Alternative", raw: customer?.alternative_phone?.trim() },
    ].filter((line): line is { label: string; raw: string } => !!line.raw);
    if (!lines.length) return null;

    return (
        <Box sx={{ mt: 1, mb: 2, display: "flex", flexDirection: "column", gap: 0.5 }}>
            {title && (
                <Typography variant="subtitle2" component="div">
                    {title}
                </Typography>
            )}
            {lines.map((line) => (
                <PhoneCheckLine key={line.label} label={line.label} raw={line.raw} />
            ))}
        </Box>
    );
}
